import Vue from "vue";
import Router from "vue-router";
import Register from "./views/Register.vue";
import ResetPassword from "./views/ResetPassword.vue";
import store from "@/store";
import Meta from "vue-meta";
import Dashboard from "./views/Dashboard.vue";
import AuthService from "./services/auth.service";

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: "/",
      name: "home",
      component: Dashboard,
      meta: {
        allowed: [
          "Global Support Manager",
          "Broker Support Admin",
          "Broker Plan Creator",
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
          "Member Portal Admin",
          "Member Portal Users Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/home",
      name: "homeWithPlan",
      component: Dashboard,
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Global Support Manager",
          "Broker Support Admin",
          "Broker Plan Creator",
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
          "Member Portal Admin",
          "Member Portal Users Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/email-callback/:reportType/:reportId",
      name: "emailCallback",
      component: () =>
        import(
          /* webpackChunkName: "reports" */ "./views/EmailCallbackView.vue"
        ),
      props: (r) => ({
        planId: r.params.planId,
        reportId: r.params.reportId,
        reportType: r.params.reportType,
      }),
      meta: {
        allowed: [
          "Global Support Manager",
          "Broker Support Admin",
          "Broker Plan Creator",
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
        ],
      },
    },
    {
      path: "/select-plan",
      name: "selectPlan",
      component: () =>
        import(/* webpackChunkName: "about" */ "./views/SelectPlan.vue"),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
          "Documents Management Admin",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
          "Member Portal Admin",
          "Member Portal Users Admin",
        ],
      },
    },
    {
      path: "/privacy-policy",
      name: "privacyPolicy",
      component: () => import("./views/PrivacyPolicy"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/terms-conditions",
      name: "termsConditions",
      component: () => import("./views/TermsConditions"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/register-with-token/:token",
      name: "registerWithToken",
      component: Register,
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/forgot-password/:token",
      name: "forgotPasswordWithToken",
      component: Register,
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/reset-password",
      name: "resetPassword",
      component: ResetPassword,
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/first-login/:token/:broker/:id",
      name: "firstLogin",
      component: () => import("./views/FirstLogin.vue"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/forgot-password",
      name: "forgotPassword",
      component: () => import("./views/ForgotPassword"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/forgot-email",
      name: "forgotEmail",
      component: () => import("./views/ForgotEmail"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/my-account",
      name: "myAccount",
      component: () => import("./views/MyAccount.vue"),
      meta: {
        allowed: [],
      },
    },
    {
      path: "/my-tickets",
      name: "myTickets",
      component: () => import("./views/MyTickets.vue"),
      meta: {
        allowed: [],
      },
    },
    {
      path: "/organizations",
      name: "organizations",
      component: () => import("./views/Organizations.vue"),
      meta: {
        allowed: ["Global Support Manager"],
      },
    },
    {
      path: "/users",
      name: "users",
      component: () => import("./views/Users.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Support Admin"],
      },
    },
    {
      path: "/users/:brokerId",
      name: "brokerUsers",
      component: () => import("./views/Users.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Support Admin"],
      },
    },
    {
      path: "/tags",
      name: "tags",
      component: () => import("./views/Tags.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Support Admin"],
      },
    },
    {
      path: "/opt-outs/:planId/reimbursements",
      name: "opt-out-reimbursements",
      component: () => import("./views/Reimbursements.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Opt-out Admin",
          "Opt-out Viewer",
        ],
      },
    },
    {
      path: "/opt-outs/:planId",
      name: "opt-outs",
      component: () => import("./views/OptOuts.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Opt-out Admin",
          "Opt-out Viewer",
        ],
      },

    },
    {
      path: "/opt-outs/:planId/settings",
      name: "opt-out-settings",
      component: () => import("./views/OptOutSettings.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Opt-out Admin",
        ],
      },

    },
    {
      path:"/opt-outs/:planId/optOuts",
      name:"opt-outs-list",
      component:()=>import("./views/OptOutsList.vue"),
      props:(r)=>({
        planId:r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
        ],
      },
    },
    {
      path: "/plan/:planId/members",
      name: "members",
      component: () => import("./views/Members.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/members-table",
      name: "membersManagement",
      component: () => import("./views/MembersManagement.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/members/:id",
      name: "memberView",
      component: () => import("./components/MemberView.vue"),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
        ],
      },
      props: (r) => ({
        planId: r.params.planId,
        id: r.params.id,
      }),
    },
    // {
    //   path: "/members-table/:plan",
    //   name: "membersStatusTableWithPlan",
    //   component: () => import("./views/MembersManagement.vue"),
    //   meta: {
    //     allowed: [
    //       "Broker Plan Admin",
    //       "Broker Plan Manager",
    //       "Employer Plan Admin",
    //     ],
    //   },
    // },
    {
      path: "/plan/:planId/members-import-wizard",
      name: "membersImportWizard",
      component: () => import("./views/MembersImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/dependants-import-wizard",
      name: "dependantsImportWizard",
      component: () => import("./views/DependantsImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/members-import-salaries",
      name: "membersImportSalaries",
      component: () => import("./components/MembersImportSalaries.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/billing-premiums-records",
      name: "premiumsStatusTable",
      component: () => import("./views/PremiumsStatusTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Premiums Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/billing-premiums-archive",
      name: "premiumsArchive",
      component: () => import("./views/PremiumsArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Premiums Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/billing-remittances-records",
      name: "remittancesHistory",
      component: () => import("./views/RemittancesHistory.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/billing-remittances-archive",
      name: "remittancesArchive",
      component: () => import("./views/RemittancesArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/billing-wizard",
      name: "billingWizard",
      component: () => import("./views/BillingWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/revise-wizard",
      name: "reviseWizard",
      component: () => import("./views/ReviseWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/billing-payment-tracking",
      name: "paymentTracking",
      component: () => import("./views/PlanBalance.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/billing-archive",
      name: "billingArchive",
      component: () => import("./views/BillingArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/billing",
      name: "billing",
      component: () => import("./views/Billing.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/billing-commissions-table",
      name: "commissionsTable",
      component: () => import("./views/CommissionsTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/billing-commissions-archive",
      name: "commissionsArchive",
      component: () => import("./views/CommissionsArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/plan-parameters",
      name: "planParameters",
      component: () => import("./views/PlanParameters.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/plan-general-parameters/:edit",
      name: "planGeneralParameters",
      component: () => import("./views/PlanGeneralParameters.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/plan-trust-fund",
      name: "trustFund",
      component: () => import("./views/TrustFund.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager"],
      },
    },
    // {
    //   path: '/plan/:planId/plan-employers',
    //   name: 'employers',
    //   component: () => impor./views/EmployersTable.vuevue'),
    //   props: (r) => ({
    //     planId: r.params.planId,
    //   }),
    //   meta: {
    //     allowed: ['Broker Plan Manager'],
    //   },
    // },
    {
      path: "/plan/:planId/plan-union-locals",
      name: "unionLocals",
      component: () => import("./views/UnionLocals.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Manager",
          "Broker Plan Admin",
          "Broker Plan Creator",
        ],
      },
    },
    {
      path: "/plan/:planId/plan-insurance-carriers",
      name: "insuranceCarriers",
      component: () => import("./views/InsuranceCarriers.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/plan-coverage",
      name: "coverage",
      component: () => import("./views/Coverage.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-t4a-wizard",
      name: "t4aWizard",
      component: () => import("./views/T4AWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-t4a-archive",
      name: "t4aArchive",
      component: () => import("./views/T4AArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-demographics",
      name: "demographics",
      component: () => import("./views/Demographics.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-demographics-archive",
      name: "demographicsArchive",
      component: () => import("./views/DemographicsArchive.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-eligibility",
      name: "eligibility",
      component: () => import("./views/Eligibility.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports",
      name: "reports",
      component: () => import("./views/Reports.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/reports-admin-files",
      name: "adminFiles",
      component: () => import("./views/AdminFiles.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Employer Plan Admin",
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Documents Management Admin",
          "Employer Members Viewer",
        ],
      },
    },
    // {
    //   path: "/support-monitoring",
    //   name: "monitoring",
    //   component: () => import("./views/Monitoring.vue"),
    //   meta: {
    //     allowed: ["Global Support Manager", "Broker Support Admin"],
    //   },
    // },
    // {
    //   path: "/help",
    //   name: "help",
    //   component: () => import("./views/Help.vue"),
    //   meta: {
    //     allowed: [],
    //   },
    // },
    {
      path: "/wip",
      name: "wip",
      component: () => import("./views/WIP.vue"),
      meta: {
        allowed: [],
      },
    },
    {
      path: "/plan/:planId/portal",
      name: "portal",
      component: () => import("./views/Portal.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/download-centre",
      name: "downloadCenter",
      component: () => import("./views/DownloadCenter.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/theme-settings",
      name: "themeSettings",
      component: () => import("./components/ThemeSettings.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/sidebar-items",
      name: "sidebarItems",
      component: () => import("./views/SidebarItems.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-pages",
      name: "portalPages",
      component: () => import("./views/PortalPages.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-news",
      name: "portalNews",
      component: () => import("./views/PortalNews.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-notifications",
      name: "portalNotifications",
      component: () => import("./views/PortalNotifications.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-contacts",
      name: "portalContacts",
      component: () => import("./views/PortalContacts.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-coverage",
      name: "portalCoverage",
      component: () => import("./views/PortalCoverage.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/portal-welcome-kit",
      name: "welcomeKit",
      component: () => import("./views/WelcomeKit.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/welcome-kits/:cms",
      name: "welcomeKitNew",
      component: () => import("./views/WelcomeKitNew.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
        requiresPlan: true,
      },
    },
    {
      path: "/plan/:planId/mportal-users",
      name: "usersManagement",
      component: () => import("./views/UsersManagement.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Manager",
          "Member Portal Admin",
          "Member Portal Users Admin",
        ],
        requiresPlan: true,
      },
    },
    {
      path: "/icons",
      name: "icons",
      component: () => import("./views/IconsPage.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/images",
      name: "images",
      component: () => import("./views/ImagesPage.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/select-broker/:route",
      name: "selectBroker",
      component: () => import("./views/SelectBroker.vue"),
      meta: {
        allowed: ["Global Support Manager"],
      },
    },
    {
      path: "/plan/:planId/drugs/:cms",
      name: "drugs",
      component: () => import("./views/Drugs.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
        requiresPlan: true,
      },
    },
    {
      path: "/plan/:planId/benefit-cards/:cms",
      name: "benefitCards",
      component: () =>
        import("./components/BenefitCards/BenefitCardsTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
        requiresPlan: true,
      },
    },
    {
      path: "/plan/:planId/providers/:cms",
      name: "providers",
      component: () => import("./components/Providers/ProvidersTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
        requiresPlan: true,
      },
    },
    {
      path: "/plans/:broker",
      name: "plans",
      component: () => import("./views/Plans.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/plans/:broker/:plan/:status",
      name: "planWizardView",
      component: () => import("./views/PlanWizardView.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/insurance-carriers-index",
      name: "insuranceCarriersIndex",
      component: () => import("./views/InsuranceCarriersIndex.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
      },
    },
    {
      path: "/taxes",
      name: "taxes",
      component: () => import("./views/Taxes.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
      },
    },
    {
      path: "/pension-regulators",
      name: "pensionRegulators",
      component: () => import("./views/PensionRegulators.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
      },
    },
    {
      path: "/claims-admins",
      name: "claimAdmins",
      component: () => import("./views/ClaimAdmins.vue"),
      meta: {
        allowed: ["Global Support Manager"],
      },
    },
    {
      path: "/underwriters",
      name: "underwritersView",
      component: () => import("./views/UnderwritersView.vue"),
      meta: {
        allowed: ["Global Support Manager"],
      },
    },
    {
      path: "/plan/:planId/add-member",
      name: "addMemberWizard",
      component: () => import("./views/AddMemberWizardView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/search-members",
      name: "searchMembersForm",
      component: () => import("./components/SearchMembersForm.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/search-beneficiaries",
      name: "searchBeneficiariesForm",
      component: () => import("./components/SearchBeneficiariesForm.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Beneficiaries Administrator",
        ],
      },
    },
    {
      path: "/plan/:planId/members-files",
      name: "membersFiles",
      component: () => import("./views/MembersDownloadCenter.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/documents-centre",
      name: "membersDownloadCenter",
      component: () => import("./views/DocumentsCentre.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/plan-files/:mode",
      name: "planFiles",
      component: () => import("./views/PlanDownloadCenter.vue"),
      props: (r) => ({
        planId: r.params.planId,
        mode: r.params.mode,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
          "Employer Members Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/contributions-control",
      name: "contributions",
      component: () => import("./views/Contributions.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/contributions-view",
      name: "contributionsView",
      component: () => import("./views/ContributionsView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/contributions-terms-setup",
      name: "contributionsSetup",
      component: () =>
        import("./components/ContributionsControl/TermsSetup.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/contributions-wizard",
      name: "contributionsWizard",
      component: () => import("./views/ContributionsWizardView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/balance-payments",
      name: "balancePayments",
      component: () => import("./views/BalancePayments.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/balance-view",
      name: "balanceView",
      component: () => import("./views/BalanceView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/payments-view",
      name: "paymentsView",
      component: () => import("./views/PaymentsView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/share-files/:token",
      name: "shareFiles",
      component: () => import("./views/ShareFiles.vue"),
      meta: {
        public: true, // Allow access to even if not logged in
        onlyWhenLoggedOut: false,
      },
    },
    {
      path: "/share-files-user/:token",
      name: "shareFilesUser",
      component: () => import("./views/ShareFilesUser.vue"),
      meta: {
        allowed: [
          "Global Support Manager",
          "Broker Support Admin",
          "Broker Plan Creator",
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Documents Management Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
          "Member Portal Admin",
        ],
        requiresPlan: false,
      },
    },
    {
      path: "/share-files-guest/:token",
      name: "shareFilesGuest",
      component: () => import("./views/ShareFilesGuest.vue"),
      meta: {
        public: true, // Allow access to even if not logged in
        // onlyWhenLoggedOut: true,
      },
    },
    {
      path: "/plan/:planId/pending-self-payments",
      name: "pendingSelfPayments",
      component: () => import("./views/PendingSelfPayments.vue"),
      props: (r) => ({
        planId: r.params.planId,
        deposited: r.params.deposited,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/hours-import-wizard",
      name: "hoursImportWizard",
      component: () => import("./components/HoursImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/hours-import-wizard/:pendingImportId",
      name: "hoursImportWizardWithId",
      component: () => import("./components/HoursImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
        pendingImportId: r.params.pendingImportId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/pending-hours-tasks",
      name: "pendingHoursTasksTable",
      component: () => import("./views/PendingHoursTasksTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    // {
    //   path: "/plan/:planId/hours-bank",
    //   name: "hoursBank",
    //   component: () => import("./views/HoursBank.vue"),
    //   props: (r) => ({
    //     planId: r.params.planId
    //   }),
    //   meta: {
    //     allowed: [
    //       "Broker Plan Admin",
    //       "Broker Plan Manager"
    //     ],
    //   },
    // },
    {
      path: "/plan/:planId/employers",
      name: "employers",
      component: () => import("./views/Employers.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Employer Members Viewer",
          "Employer Premiums Viewer",
          "Employer Billing Viewer",
        ],
      },
    },
    {
      path: "/plan/:planId/employers/view",
      name: "employersView",
      component: () => import("./views/EmployersView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/employers/details/:employerId",
      name: "employerDetails",
      component: () => import("./components/Employers/EmployerDetails.vue"),
      props: (r) => ({
        planId: r.params.planId,
        id: r.params.employerId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/employers/search",
      name: "searchEmployersForm",
      component: () => import("./components/Employers/SearchEmployersForm.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/employers/new",
      name: "employerWizard",
      component: () => import("./components/Employers/EmployerWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/employers-files",
      name: "employersFiles",
      component: () => import("./views/EmployersDownloadCenter.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Admin", "Broker Plan Manager"],
      },
    },
    {
      path: "/plan/:planId/portal-content",
      name: "portalContent",
      component: () => import("./components/Portal/PortalContent.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/i3-series-claims",
      name: "iSeriesClaimsView",
      component: () => import("./views/iSeriesClaimsView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
    },
    {
      path: "/claims/history",
      name: "claimsTable",
      component: () => import("./views/ClaimsHistoryView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
    },
    {
      path: "/guides",
      name: "guidesView",
      component: () => import("./views/GuidesView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
    },
    {
      path: "/plan/:planId/portal-view",
      name: "portalView",
      component: () => import("./views/PortalView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: [
          "Broker Plan Manager",
          "Member Portal Admin",
          "Member Portal Users Admin",
        ],
      },
    },
    {
      path: "/plan/:planId/contact-information-import-wizard",
      name: "contactInformationImportWizard",
      component: () =>
        import("./components/DataImport//ContactInformationImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/union-local-membership-import-wizard",
      name: "unionLocalMembershipImportWizard",
      component: () =>
        import("./components/DataImport/UnionLocalMembershipImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/benefits",
      name: "benefitsView",
      component: () => import("./views/BenefitsView.vue"),
      meta: {
        allowed: ["Global Support Manager"],
        requiresPlan: false,
      },
    },
    {
      path: "/plan-benefits-rules/:broker",
      name: "planBenefitsRules",
      component: () => import("./views/PlanBenefitsRulesView.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Plan Manager"],
        requiresPlan: false,
      },
    },
    {
      path: "/claim-submission/:coverageType",
      name: "claimSubmission",
      component: () => import("@/views/ClaimSubmissionView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
      props: (r) => ({
        coverageType: r.params.coverageType,
      }),
    },
    {
      path: "/claim-submission/:coverageType/:memberId",
      name: "memberClaimSubmission",
      component: () => import("@/views/ClaimSubmissionView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
      props: (r) => ({
        coverageType: r.params.coverageType,
        memberIdentityId: r.params.memberId,
      }),
    },
    {
      path: "/plan-drugs/:broker",
      name: "planDrugs",
      component: () => import("./views/NewDrugs.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Plan Manager"],
        requiresPlan: false,
      },
      props: (r) => ({
        brokerId: r.params.broker,
      }),
    },
    {
      path: "/claims/payments",
      name: "claimsPayments",
      component: () => import("./components/Claims/BatchPaymentsTable.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
    },
    {
      path: "/providers",
      name: "providersView",
      component: () => import("./views/ProvidersView.vue"),
      meta: {
        allowed: ["Global Support Manager"],
        requiresPlan: false,
      },
    },
    {
      path: "/proofs/:proof/:name/:file",
      name: "proofView",
      component: () => import("./views/ProofFileView.vue"),
      meta: {
        allowed: ["Global Support Manager"],
        requiresPlan: false,
      },
      props: (r) => ({
        uploadName: r.params.proof,
        fileName: r.params.name,
        fileId: r.params.file,
      }),
    },
    {
      path: "/claims/:planId",
      name: "claimsView",
      component: () => import("./views/ClaimsView.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
      props: (r) => ({
        planIdentityId: r.params.planId,
      }),
    },
    {
      path: "/helpdesk/:planId",
      name: "helpdesk",
      component: () => import("./views/HelpdeskView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
    },
    {
      path: "/plan/:planId/hsa-import-wizard",
      name: "hsaImportWizard",
      component: () => import("./views/HsaImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/portal/configuration",
      name: "portalPlanSettings",
      component: () => import("./components/Portal/PortalConfiguration.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Member Portal Admin"],
      },
    },
    {
      path: "/plan/:planId/employment-information-import-wizard",
      name: "employmentInformationImportWizard",
      component: () =>
        import("./components/DataImport/EmploymentInformationImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/terminations-import-wizard",
      name: "terminationsImportWizard",
      component: () =>
        import("./components/DataImport/TerminationsImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/activity-status-import-wizard",
      name: "activityStatusImportWizard",
      component: () =>
        import("./components/DataImport/ActivityStatusImportWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/claims/payments/reports",
      name: "claimsReports",
      component: () => import("./components/Claims/ClaimsReports.vue"),
      meta: {
        allowed: ["Claims Administrator"],
        requiresPlan: false,
      },
    },
    {
      path: "/plan/:planId/invoices",
      name: "invoicesView",
      component: () => import("./views/InvoicesView.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Broker Plan Admin"],
      },
    },
    {
      path: "/plan-fees-configuration/:broker",
      name: "planFeesConfiguration",
      component: () => import("./views/PlanFeesConfiguration.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/invoices",
      name: "invoicesAdminView",
      component: () => import("./views/InvoicesAdminView.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Support Admin"],
      },
    },
    {
      path: "/claims-settings/:broker",
      name: "claimsSettings",
      component: () => import("./views/ClaimsSettings.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/sick-payment-settings/:broker",
      name: "sickPaymentSettings",
      component: () => import("./views/SickPaymentSettings.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/plan-guides-assignments/:broker",
      name: "planGuidesAssignments",
      component: () => import("./views/PlanGuidesAssignments.vue"),
      meta: {
        allowed: ["Global Support Manager", "Broker Plan Creator"],
        requiresPlan: false,
      },
    },
    {
      path: "/brokers-index",
      name: "brokersIndex",
      component: () => import("./views/BrokersIndex.vue"),
      meta: {
        allowed: ["Broker Plan Creator"],
      },
    },
    {
      path: "/plan/:planId/evaluate-eligibility-hours-bank-wizard",
      name: "evaluateEligibilityHourBankWizard",
      component: () => import("./views/EvaluateEligibilityHourBankWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/eligibility-hours-bank-evaluations",
      name: "eligibilityHourBankEvaluations",
      component: () =>
        import("./views/EligibilityHourBankEvaluationsTable.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/eligibility-bank-credit-hours",
      name: "eligibilityBankCreditHours",
      component: () => import("./views/EligibilityBankCreditHours.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/reevaluate-eligibility-hours-bank-wizard",
      name: "reevaluateEligibilityHourBankWizard",
      component: () =>
        import("./views/ReevaluateEligibilityHourBankWizard.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
    {
      path: "/plan/:planId/eligibility-bank-minimum-hour-report",
      name: "eligibilityBankMinimumHourReport",
      component: () => import("./views/EligibilityBankMinimumHourReport.vue"),
      props: (r) => ({
        planId: r.params.planId,
      }),
      meta: {
        allowed: ["Broker Plan Manager", "Employer Plan Admin"],
      },
    },
  ],
});

function loadPreferences(userLanguage) {
  let lang = store.getters["common/language"];

  if (lang === undefined || lang === null) {
    const settingsJson = window.localStorage[`language`];

    if (settingsJson !== undefined) {
      const storageLang = JSON.parse(settingsJson);
      // console.log(storageLang);
      store._actions["common/setLanguage"][0](storageLang);
      return;
    }
    if (userLanguage) {
      store._actions["common/setLanguageFromUrl"][0](userLanguage);
      return;
    }
    let browserLang = navigator.language || navigator.userLanguage;
    let languages = store.getters["common/languages"];
    if (browserLang) {
      let browserLanguage = languages.filter((o) => o.language === browserLang);
      if (browserLanguage) {
        store._actions["common/setLanguage"][0](browserLanguage);
        return;
      }
    }
    store._actions["common/setLanguage"][0]({
      language: "en",
      title: "English",
    });
  }
}

router.beforeEach(async (to, from, next) => {
  const isPublic = to.matched.some((record) => record.meta.public);
  const isPrivateRoute = !isPublic;
  const onlyWhenLoggedOut = to.matched.some((record) => {
    return record.meta.onlyWhenLoggedOut;
  });
  const user = await AuthService.getUser();
  const isLoggedIn = user !== null && !user.expired;
  store.dispatch("auth/setIsLoggedIn", isLoggedIn);

  if (isLoggedIn) {
    if (!user.profile.role) {
      return AuthService.logout();
    }

    if (onlyWhenLoggedOut) {
      return next("/");
    }

    loadPreferences(user.profile.language);
    AuthService.handleUser(user);

    if (isPrivateRoute) {
      console.log(JSON.stringify(to));
      const allowed = to.meta.allowed || [];
      const hasPlan =
        !!store.getters["common/plan"] ||
        (to.params.planId !== null && to.params.planId !== undefined);
      console.log(hasPlan);
      // not really a private route then
      if (allowed.length === 0) {
        return next();
      }

      if (
        AuthService.hasAccess(user, allowed, [
          "Broker Plan Admin",
          "Broker Plan Manager",
          "Employer Plan Admin",
          "Member Portal Admin",
          "Employer Members Viewer",
          "Employer Billing Viewer",
          "Employer Premiums Viewer",
          "Documents Management Admin",
          "Member Portal Users Admin",
        ])
      ) {
        console.log(hasPlan);
        if (!hasPlan) {
          if (to.meta.requiresPlan === false) {
            return next();
          }

          // go to select plan but can't go anywhere after...
          if (to.name !== "selectPlan") {
            if (from.name === "selectPlan") {
              return next(new Error("need plan"));
            }

            if (to.name === "emailCallback") {
              return next();
            }

            return next("/select-plan");
          }
        }
      }

      if (user.profile.confirmed !== "True") {
        if (to.name !== "resetPassword") {
          return next({
            path: "/reset-password",
          });
        }
      }

      var found = false;
      if (Array.isArray(store.getters["auth/role"])) {
        store.getters["auth/role"].forEach((role) => {
          if (allowed && allowed.indexOf(role) > -1) {
            found = true;
          }
        });
      } else if (allowed && allowed.indexOf(store.getters["auth/role"]) > -1) {
        found = true;
      }

      if (!found) {
        return store.dispatch("auth/logout");
      }

      return next();
    }
  }

  if (isPrivateRoute) {
    let state = {};
    if (to.query.redirect) {
      state.redirectUrl = window.location.href;
    }

    return AuthService.login(state);
  } else if (isPublic) {
    return next();
  }
});

Vue.use(Meta);
export default router;
